import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

type User = { _id: string; name: string; email: string } | null;

type AuthContextType = {
  user: User;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (name: string, email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [user, setUser] = useState<User>(null);

  const login = useCallback(async (email: string, password: string) => {
    const axios = (await import('axios')).default;
    const { API } = await import('../config/api');
    try {
      const res = await axios.post(API.auth.login, { email, password });
      if (res.data.success && res.data.token) {
        localStorage.setItem('token', res.data.token);
        if (res.data.user) localStorage.setItem('user', JSON.stringify(res.data.user));
        setToken(res.data.token);
        setUser(res.data.user || null);
        return { success: true, message: res.data.message || 'Logged in successfully.' };
      }
      return { success: false, message: res.data.message || 'Login failed' };
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      return { success: false, message: err.response?.data?.message || err.message || 'Login failed' };
    }
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const axios = (await import('axios')).default;
    const { API } = await import('../config/api');
    try {
      const res = await axios.post(API.auth.register, { name, email, password });
      if (res.data.success) return { success: true, message: res.data.message || 'Registration successful. You can now log in.' };
      return { success: false, message: res.data.message || 'Registration failed' };
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      return { success: false, message: err.response?.data?.message || err.message || 'Registration failed' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }
    const raw = localStorage.getItem('user');
    if (raw) {
      try {
        setUser(JSON.parse(raw));
      } catch {
        setUser(null);
      }
    }
  }, [token]);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
